import { createApp, watch } from 'vue';
import App from './App.vue';
import { router } from './router';
import { installLegacyNavigationBridge } from './router/legacyNavigationBridge';
import { unmountAll } from './mount/zoneMount';
import { log } from '@/utils/core/vueLogger';
import { createMyVuetify, resolveVuetifyLocale } from './plugins/vuetify';
import { patchVuetifyClickOutside } from '@/utils/app/patchVuetifyClickOutside';
import { iframeDataBridge } from './iframe/iframeDataBridge';
import { i18n } from './i18n';
import { initI18n } from './i18n/bootstrap';
import { AllEnterpriseModule, IntegratedChartsModule, ModuleRegistry } from 'ag-grid-enterprise';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { initializeSync } from './sync/dataSync';
import { startConnectivity } from '@/sync/connectivity';
import { installConnectivityHost } from '@/iframe/legacyConnectivity';
import { installLegacyLoaderHost } from '@/iframe/legacyLoader';
import { bootSession, enterApplication, session } from '@/utils/auth/sessionService';
import { registerServiceWorker } from '@/utils/app/registerServiceWorker';
import { initializeAgGridEnterprise } from '@/utils/agGrid/agGridEnterprise';
import { preloadMaterialSymbolsFont } from '@/utils/app/materialSymbolsPreloader';
import { initSentry, setSentryUser } from '@/utils/app/sentry';
import { initMemoryPressure } from '@/utils/app/memoryPressure';
import { clearAllMemoryCaches } from '@/utils/core/cacheManager';
import { releaseInMemoryFileUrls } from './api/core/file';
import { pinia } from './stores';

import './styles/root.scss';
import './styles/main.scss';

patchVuetifyClickOutside();

const app = createApp(App);

if (import.meta.env.DEV) {
	app.config.devtools = true;
}

ModuleRegistry.registerModules([AllEnterpriseModule, IntegratedChartsModule.with(AgChartsEnterpriseModule)]);

const userLocale = resolveVuetifyLocale(iframeDataBridge.getCurrentUser()?.locale);

// Router ↔ legacy evtChangeView sync (md/ROUTER_MIGRATION.md). Installed before app.use(router), which
// starts the initial navigation; the first legacy route opens the application through the Vue session.
installLegacyNavigationBridge(router, {
	getIframeWindow: () => iframeDataBridge.getIframeWindow(),
	unmountAll,
	log,
	appointmentType: () => iframeDataBridge.enumAppointment.type.CL,
	enterApplication: () => enterApplication(),
});

// Vue drives the online/offline state (md/CONNECTIVITY.md): exposed before the iframe exists, so the legacy
// reads it from its very first script.
installConnectivityHost();
startConnectivity();

// The legacy full-screen loaders are the Vue EcosShelfProgress (src/iframe/legacyLoader.js).
installLegacyLoaderHost();

// Vue owns the session (md/ROUTER_MIGRATION.md "Phase 2c"): the SSO handoff cookies and query are consumed
// before the iframe exists, the session is resolved once the legacy gateway is there (the router waits for it).
bootSession();

app.use(router);
app.use(i18n);
app.use(createMyVuetify(userLocale));
app.use(pinia);

initSentry(app, router);

app.mount('#app');

watch(
	() => session.user,
	(user) => setSentryUser(user, user?.customer),
	{ immediate: true }
);
initializeAgGridEnterprise();
void preloadMaterialSymbolsFont();

import { initIframeViewObserver } from './iframe/iframeWatcher';
initIframeViewObserver();

// Relay BO ↔ legacy-iframe postMessages across the extra Vue layer (appointment
// visit/assort dispatch + the webAppReady handshake that flips ecos.webapp.status).
import { initBoMessageRelay } from './iframe/boMessageRelay';
initBoMessageRelay();

// Queue "send DVP by mail" requests made while offline (plan de vente / bon de
// commande / plan promo) so salesHelper.dvpMail.send can replay them at next sync.
import { initDvpMailQueue } from './api/communication/mail.js';
initDvpMailQueue();

// Load the single active language for Vue (reactive) and push it into the legacy
// iframe once it is ready. Fire-and-forget: components update when messages arrive.
void initI18n();

initializeSync().catch(console.error);

// Shed RAM before iOS jetsam kills the WebContent process. The native WKWebView shell
// (separate repo) calls window.__ecosMemoryWarning() / posts { type: 'MEMORY_WARNING' };
// see src/utils/app/memoryPressure.js for the native contract.
initMemoryPressure([releaseInMemoryFileUrls, clearAllMemoryCaches]);

registerServiceWorker();
